import {
  Info,
  Store,
  ClipboardList,
  CreditCard,
  TruckElectric,
  UserCircle,
  ShoppingCart,
  ShieldCheck,
  Repeat2Icon,
  HandCoins,
} from 'lucide-react';
import { HelpFaqT } from './types';
import settings from '@/settings';
import i18n from '@/settings/i18n';

const t = i18n.init('help');

const returnDays = settings.free_return_days;
const warrantyMonth = settings.warranty_month;

const CREDIT_CARD_KEYS = ['visa', 'mastercard', 'american-express'];
const DIRECT_KEYS = ['paypal', 'apple-pay'];
const INSTALLMENT_KEYS = ['afterpay', 'klarna', 'clear-pay', 'affirm'];

const joinWithAnd = (items: string[]) => {
  if (items.length <= 1) {
    return items.join('');
  }

  return `${items.slice(0, -1).join(', ')} ${t`and`} ${
    items[items.length - 1]
  }`;
};

const joinBase = (items: string[]) => {
  if (items.length === 2) {
    return joinWithAnd(items);
  }

  return items.join(', ');
};

const paymentMethodsSentence = () => {
  const methods = settings.payment_methods as Record<string, string>;
  const hasCreditCard = CREDIT_CARD_KEYS.some((k) => k in methods);
  const directMethods = DIRECT_KEYS.filter((k) => k in methods).map(
    (k) => methods[k],
  );
  const installmentMethods = INSTALLMENT_KEYS.filter((k) => k in methods).map(
    (k) => methods[k],
  );

  const baseItems = [
    ...(hasCreditCard ? [t`a credit card`] : []),
    ...directMethods,
  ];
  const base = joinBase(baseItems);

  const isAfterpayOnly =
    installmentMethods.length === 1 && 'afterpay' in methods;

  if (installmentMethods.length === 0) {
    return t`Pay securely with ${{ methods: base }}.`;
  }

  if (isAfterpayOnly) {
    return t`Pay securely with ${{
      methods: base,
    }}, or enjoy free installments via ${{
      installment: installmentMethods[0],
    }}.`;
  }

  return t`Pay securely with ${{
    methods: base,
  }}. We also offer flexible installment plans through ${{
    installments: joinWithAnd(installmentMethods),
  }}.`;
};

const listClass = 'list-disc list-inside ml-6 space-y-1';

const isAu = settings.store === 'reebelo-au';
const isUk = settings.store === 'reebelo-uk';
const isUs = settings.store === 'reebelo-us';
const hasReebeloCare = [
  'reebelo-au',
  'reebelo-us',
  'reebelo-ca',
  'reebelo-nz',
].includes(settings.store);

export const getHelpFaqs = (): HelpFaqT[] => [
  {
    sectionTitle: t`About Reebelo`,
    icon: Info,
    sectionContent: [
      {
        question: t`What is Reebelo?`,
        answer: (
          <>
            <p>
              {t`Reebelo is your trusted marketplace for premium refurbished and
              certified pre-owned products — all at unbeatable prices. From
              electronics and kitchen appliances to sports gear and fashion, we
              make it easy to shop smarter and more sustainably.`}
            </p>
            <p>
              {t`Every item is sourced from carefully vetted local experts known
              for their refurbishing quality, fast fulfillment, and
              eco-conscious practices. Each product is professionally restored,
              thoroughly tested, and backed by warranty, so you can shop with
              total peace of mind.`}
            </p>
            <p>
              {t`Join over`} <strong>{t`500,000 happy customers`}</strong>{' '}
              {t`worldwide and experience the Reebelo difference. With an`}{' '}
              <strong>{t`‘Excellent’ Trustpilot rating`}</strong>
              {t`, we’re proud to help people save money, reduce waste, and get
              the tech (and more) they love, without compromise.`}
            </p>
          </>
        ),
      },
      {
        question: t`Why should I shop certified pre-owned?`,
        answer: (
          <>
            <p>
              {t`Get the same great performance and features as new, for a fraction
              of the price. Shopping certified pre-owned is a smarter, more
              sustainable way to upgrade without breaking the bank.`}
            </p>
            <p>
              {t`At Reebelo, every product is professionally cleaned,`}{' '}
              <strong>{t`quality-tested`}</strong>
              {t`, and`}{' '}
              <strong>
                {t`backed by our warranty and ${{
                  returnDays,
                }}-day free returns`}
              </strong>
              {t`, so you can shop risk-free and ensure your purchase is the
              perfect fit. It’s better for your budget, better for the planet,
              and always risk-free.`}
            </p>
          </>
        ),
      },
      {
        question: t`Does Reebelo have a Physical Store?`,
        answer: (
          <p>
            {t`Reebelo sells exclusively online. We aim to create a trusted digital
            marketplace that brings the utmost transparency where you can buy
            premium refurbished and certified pre-owned products with peace of
            mind.`}
          </p>
        ),
      },
    ],
  },
  {
    sectionTitle: t`Reebelo’s Vendors`,
    icon: Store,
    sectionContent: [
      {
        question: t`Am I buying products directly from Reebelo?`,
        answer: (
          <p>
            {t`While Reebelo manages the entire customer experience – from
            showcasing products and processing payments to handling warranties
            and customer service – the products themselves are supplied by our
            network of trusted, professional vendors. Think of us as your
            quality control expert and helpful guide, ensuring every device you
            see on our site meets our high standards before it reaches you.`}
          </p>
        ),
      },
      {
        question: t`How are Reebelo vendors determined?`,
        answer: (
          <p>
            {t`We're very selective about who we partner with! Our vendors are
            professional refurbishers and certified sellers who undergo a
            stringent vetting process. We assess their operational quality,
            product authenticity, refurbishment expertise, and customer service
            track record to ensure they consistently deliver top-notch devices.
            This rigorous selection process is key to providing you with the
            reliable, high-quality products you expect from Reebelo.`}
          </p>
        ),
      },
      {
        question: t`Why do product prices sometimes change?`,
        answer: (
          <p>
            {t`Product prices on Reebelo can shift occasionally due to several
            factors, much like other marketplaces! This can be influenced by
            changes in market demand for specific models and the availability of
            devices from our trusted vendors. We always strive to offer you the
            most competitive prices for quality certified pre-owned devices,
            reflecting the current market value.`}
          </p>
        ),
      },
    ],
  },
  {
    sectionTitle: t`Product Conditions, Batteries, and Accessories`,
    icon: ClipboardList,
    sectionContent: [
      {
        question: t`Why should I shop certified pre-owned with Reebelo vs a Customer-to-Customer marketplace?`,
        answer: (
          <p>
            {t`Reebelo offers certified pre-owned products that undergo rigorous
            professional inspection and come with a reliable warranty of up to
            ${{
              warrantyMonth,
            }} months. This means you get quality assurance and
            peace of mind, unlike the often uncertain purchases from individual
            sellers, which lack warranties and professional vetting. Choose
            Reebelo for a trusted, hassle-free experience with high-quality
            products, dedicated support and a clear return policy!`}
          </p>
        ),
      },
      {
        question: t`What do the product conditions mean?`,
        answer: (
          <>
            <p>
              {t`Product-specific condition definitions are always available on
              each product page. Generally, products are categorized into these
              clear conditions:`}
            </p>
            <ul className={listClass}>
              <li>
                {t`Like New - Pristine condition. Like-new look & feel, with
                little to no visible imperfections.`}
              </li>
              <li>{t`Very Good - Minimal visible wear, near mint-condition.`}</li>
              <li>
                {t`Good - Fully functional with moderate cosmetic signs of use.`}
              </li>
              <li>
                {t`New With Tags - Pristine condition with original tags, case, or
                box.`}
              </li>
              <li>
                {t`New Without Tags - No sign of wear or usage. Exactly what it is
                - new!`}
              </li>
            </ul>
            <p>
              {t`No matter the condition, all products are 100% functional, tested,
              & cleaned.`}
            </p>
          </>
        ),
      },
      {
        question: t`What do the battery conditions mean on tech products?`,
        answer: (
          <p>
            {t`All products with batteries come with at least 80% capacity for
            reliable daily use. Many products offer upgrade options to 100%.
            You're covered for ${{ warrantyMonth }} months against battery
            failure and overheating. In the unlikely event that your battery
            drains within the first 3 months more than 10% below the battery
            tier you selected, you can return your device for a full refund.
            After the coverage period, we’ll gladly connect you with trusted
            repair partners for discounted battery replacements.`}
          </p>
        ),
      },
      {
        question: t`What does my purchase come with?`,
        answer: (
          <p>
            {t`Your Reebelo purchase includes your professionally cleaned and
            inspected pre-owned product, backed by our ${{
              warrantyMonth,
            }}-month
            warranty. For tech device purchases, a certified charging cable will
            also be included to get you started!`}
          </p>
        ),
      },
      {
        question: t`Are Reebelo devices sourced locally, and will they work fully on my network?`,
        answer: (
          <p>
            {t`Reebelo devices are sourced from both local and international
            markets. All devices can be used on any network in the country you
            buy them.`}
          </p>
        ),
      },
      ...(isUs
        ? [
            {
              question: t`What is the difference between a locked and unlocked phone?`,
              answer: (
                <>
                  <p>
                    {t`Confused about locked versus unlocked phones? We’re here to
                    help clarify!`}
                  </p>
                  <p>
                    {t`A locked phone is tied to a specific mobile carrier due to a
                    software lock (also called firmware). This means you cannot
                    easily use the device on another carrier.`}
                  </p>
                  <p>
                    {t`A fully unlocked phone, on the other hand, doesn't have
                    this firmware. For these devices, you usually just need to
                    insert your SIM card to get started!`}
                  </p>
                  <p>
                    {t`Unlocked phones can be used with nearly any carrier in the
                    US and globally, offering you much more flexibility. Before
                    purchasing a device, we highly recommend checking your data
                    plan's compatibility with your carrier to ensure it
                    will work seamlessly with the device.`}
                  </p>
                </>
              ),
            },
          ]
        : []),
      {
        question: t`“Important Message” or “Unknown Part” warning on Apple devices`,
        answer: (
          <>
            <p>
              {t`Once you set up your iPhone you may see an "Important
              Message" from Apple regarding your battery, display or
              camera, e.g. "Important Battery Message - Unable to verify
              this iPhone has a genuine Apple battery" or "Unknown
              Part" under the "Parts and Service History" section
              in Settings.`}
            </p>
            <p>
              {t`We understand that seeing any warnings on your device may cause
              unease. Rest assured that these types of warnings are a normal
              occurrence for some refurbished iPhones.`}
            </p>
            <p>
              {t`These messages show when repair work has been done to the device
              and provide information on which component was replaced (e.g.
              battery, screen or camera, depending on the iPhone model).
              However, this does not mean that your battery (or other component)
              has a problem or is defective. It just means that it was replaced
              with a new component that works just as effectively as the
              original one.`}
            </p>
          </>
        ),
      },
    ],
  },
  {
    sectionTitle: t`Payments`,
    icon: CreditCard,
    sectionContent: [
      {
        question: t`What payment options do I have?`,
        answer: <p>{paymentMethodsSentence()}</p>,
      },
      {
        question: t`Does Reebelo ever offer discounts or sales?`,
        answer: (
          <>
            <p>
              {t`While we always strive to offer great deals on items listed on
              Reebelo, we do occasionally provide discount codes for extra
              savings! To apply a discount code, simply find the "Discount
              code or gift card" box during the "Payment" phase
              of checkout, enter your code, and click "Apply."`}
            </p>
            <p>
              {t`To stay updated on future promotions and special offers, be sure
              to follow us on our social media channels – that's where we
              share our latest updates and exclusive deals!`}
            </p>
          </>
        ),
      },
      {
        question: t`Does Reebelo have an option to order items in bulk?`,
        answer: (
          <>
            <p>
              {t`For customers looking to buy several devices in bulk, provided
              it's 10 or more units in a single purchase, they are welcome
              to use the code BULKORDER3 for 3% off your order total.`}
            </p>
            <p>
              {t`The order can be made directly on our site with the current
              inventory made available by our vendors.`}
            </p>
          </>
        ),
      },
    ],
  },
  {
    sectionTitle: t`Shipping`,
    icon: TruckElectric,
    sectionContent: [
      {
        question: t`How do I track my order?`,
        answer: (
          <p>
            {t`You can easily track your order within your Reebelo account by
            visiting`}{' '}
            <strong>{t`Account > My Orders`}</strong>
            {t`. Once your order has been dispatched, we’ll provide you with a
            tracking ID and a direct link to our delivery partner's tracking
            page.`}
          </p>
        ),
      },
      {
        question: t`How long will it take to receive my order?`,
        answer: (
          <p>
            {t`Generally, orders are dispatched from the vendor's warehouse
            within`}{' '}
            <strong>{t`1-2 business days`}</strong>
            {t`. After that, delivery typically takes between`}{' '}
            <strong>{t`1 to 10 days`}</strong>
            {t`, depending on your location, the vendor's location and the
            carrier used.`}
          </p>
        ),
      },
    ],
  },
  {
    sectionTitle: t`My Account`,
    icon: UserCircle,
    sectionContent: [
      {
        question: t`How do I create an account?`,
        answer: (
          <p>
            {t`Creating a Reebelo account is simple! Just`}{' '}
            <a
              href="/account/register"
              target="_blank"
              rel="noreferrer"
              className="text-link-blue-500 underline"
            >
              {t`click here`}
            </a>{' '}
            {t`to get started.`}
          </p>
        ),
      },
      {
        question: t`How do I log in to my account?`,
        answer: (
          <p>
            {t`To log in, head to`}{' '}
            <a
              href="/account/login"
              target="_blank"
              rel="noreferrer"
              className="text-link-blue-500 underline"
            >
              {t`the login page`}
            </a>
            {t`. Enter the email address you used when checking out in the
            "Sign in" form. Alternatively, if your email is connected
            to a Google account, you can simply use the "Sign in with
            Google" button.`}
          </p>
        ),
      },
      {
        question: t`What are the benefits of having a Reebelo account?`,
        answer: (
          <p>
            {t`When you sign up for a Reebelo account, you’ll get access to
            exclusive promotions and special offers through our email
            newsletter. Plus, it makes managing your orders a breeze! You can
            easily track your orders, get assistance with returns, generate
            invoices, and contact our Support Team – just set up your account
            using the email address you used for your purchase.`}
          </p>
        ),
      },
      {
        question: t`How do I unsubscribe from Reebelo’s newsletters?`,
        answer: (
          <p>
            {t`To unsubscribe, simply click the "Unsubscribe" link at the
            bottom of any Reebelo newsletter or email you've received.
            You'll be redirected to a page where you can enter your email
            address and confirm your unsubscription.`}
          </p>
        ),
      },
    ],
  },
  {
    sectionTitle: t`My Order`,
    icon: ShoppingCart,
    sectionContent: [
      {
        question: t`How do I check my order details?`,
        answer: (
          <p>
            {t`You can easily check your order details and history by logging into
            your Reebelo account and visiting "My Orders." You can
            also find your order details in the order confirmation email that
            was sent to you right after your purchase.`}
          </p>
        ),
      },
      {
        question: t`My item arrived damaged–what do I do?`,
        answer: (
          <>
            <p>
              {t`While rare, damages can occasionally happen during shipping. Not
              to worry, we're here to help! Damages can include physical
              flaws like cracked or broken screens, dents or scratches beyond
              the specified condition, or functional flaws such as a broken
              speaker or camera.`}
            </p>
            <p>
              {isAu || isUk
                ? t`If your item arrives damaged, please report the issue as soon as possible following delivery.`
                : t`If your item arrives damaged, please report the issue as soon as possible, within 4 business days following delivery.`}{' '}
              {t`This allows us to review the claim promptly and for the vendor to
              report the issue to the shipping carrier. When reporting the
              issue, please provide clear, well-lit photos of:`}
            </p>
            <ul className={listClass}>
              <li>{t`The damaged item, showing the damage clearly`}</li>
              <li>{t`The packaging, including all sides`}</li>
              <li>{t`The shipping label, including the tracking number`}</li>
            </ul>
          </>
        ),
      },
    ],
  },
  {
    sectionTitle: t`Warranty & Returns`,
    icon: Repeat2Icon,
    sectionContent: [
      {
        question: t`What is Reebelo’s return policy?`,
        answer: (
          <>
            <p>
              {t`If your purchase doesn’t meet your expectations or you change your
              mind, you can return the item free of charge within
              ${{
                returnDays,
              }} days from the delivery date–no questions asked.`}
            </p>
            <p>
              {t`For all pre-owned items, you can return your item within
              ${{ returnDays }} days for a full refund assuming it's in the same
              condition as when it was received. If the vendor's inspection
              finds the item was damaged after delivery, the return will be
              denied and the item will be sent back to you.`}
            </p>
            <p>
              {t`For electronic devices, please ensure that your product is free of
              any passcodes and iCloud/Google accounts as well as any software
              lock (iCloud/passcode/SIM Lock. etc.) and include all the
              accessories that came with your purchase.`}
            </p>
            {!isUk && (
              <p>
                {t`For Brand New devices, the item must be unopened and sealed in
                its original packaging. If the item was opened or damaged after
                receipt, the return will be denied and the item will be sent
                back to you.`}
              </p>
            )}
            <p>
              {t`To start a return, log in to your account and select “My orders”.
              Find the item you’d like to return and click “Return”. Complete
              the form as accurately as possible and provide all the requested
              information.`}
            </p>
            {isAu && (
              <p className="italic">
                {t`Important Notice: Our goods come with guarantees that cannot be
                excluded under the Australian Consumer Law (ACL). You're
                entitled to a replacement or a refund for a major failure and
                compensation for any other reasonably foreseeable loss or
                damage. You're also entitled to have the goods repaired or
                replaced if the goods fail to be of acceptable quality and the
                failure doesn't amount to a major failure.`}
              </p>
            )}
          </>
        ),
      },
      {
        question: t`Do Reebelo products come with a warranty?`,
        answer: isUk ? (
          <p>
            {t`We want you to shop with confidence so all device purchases made on
            Reebelo come with a ${{
              warrantyMonth,
            }}-Month Vendor Warranty. During
            this period, the vendor will repair the device or offer a
            replacement if repair isn't possible. You're eligible for
            a refund if the vendor can't repair or replace the device. This
            warranty is valid up to ${{ warrantyMonth }} months from your
            device's delivery date.`}
          </p>
        ) : (
          <p>
            {t`We want you to shop with confidence so all device purchases made on
            Reebelo come with a ${{
              warrantyMonth,
            }}-Month Vendor Warranty. If an
            issue arises during the warranty period, your item may be eligible
            for a repair, replacement or refund, depending on the nature of the
            issue. This warranty is valid up to ${{
              warrantyMonth,
            }} months from your
            device's delivery date.`}
          </p>
        ),
      },
      {
        question: t`What is covered (and not covered) under Reebelo’s ${{
          warrantyMonth,
        }}-Month Vendor Warranty?`,
        answer: (
          <>
            <p>
              {t`The ${{
                warrantyMonth,
              }}-Month Vendor Warranty covers all hardware and
              software defects or failures, provided that the device has no
              identifiable physical or water damage. In other words, the
              warranty is limited to operational and functional defects
              encountered in normal use of the device. Please let us know as
              soon as a defect appears. If reports are not made in a timely
              manner, the vendor may not be obligated to repair or replace your
              item.`}
            </p>
            <p>
              <strong>{t`What’s not covered?`}</strong>
              <br />
              {t`We aim for a smooth and transparent experience. Please note that
              the Vendor Warranty does not cover damage or defects resulting
              from physical impact or liquid exposure. This includes:`}
            </p>
            <ul className={listClass}>
              <li>
                {t`Any malfunction or damage caused by liquid entering the device`}
              </li>
              <li>
                {t`Gradual wear and tear or cosmetic damage that does not affect
                the device's functionality.`}
              </li>
              <li>
                {t`Any malfunction or damage resulting from drops, pressure, or
                other physical impacts`}
              </li>
              <li>
                {t`Loss, deletion, or corruption of data stored on the device`}
              </li>
            </ul>
            <p>
              {t`The ${{
                warrantyMonth,
              }}-Month Vendor Warranty may not cover your
              device if it's opened for repair or diagnosis by a third
              party (e.g. anyone other than the original vendor). Under the
              terms of the Vendor Warranty, damage resulting from the above is
              not eligible for warranty remedies. This policy is in place to
              ensure fairness and clarity for all customers.`}
            </p>
            <p>
              {t`Some devices are opened during the refurbishing process for vendor
              testing. Because of this,`}{' '}
              <strong>{t`waterproof sealing is not guaranteed`}</strong>{' '}
              {t`on our refurbished devices. While our sellers do their best to
              refurbish them with the best components, waterproofing isn't
              guaranteed. As a result,`}{' '}
              <strong>
                {t`any water damage to your device will void your warranty`}
              </strong>
              {t`. We encourage you to handle your device with care to ensure it
              remains protected under the ${{ warrantyMonth }}-Month Vendor
              Warranty.`}
            </p>
            <p>
              {t`To start a warranty claim, log in to your account and select “My
              orders”. Find the item you’d like to make a claim on and click
              “Warranty Claim”. Complete the form as accurately as possible and
              provide all the requested information.`}
            </p>
          </>
        ),
      },
      ...(hasReebeloCare
        ? [
            {
              question: t`What is the ReebeloCare Extended Warranty?`,
              answer: (
                <>
                  <p>
                    {t`For enhanced protection on select products, we offer
                    ReebeloCare Extended Warranty for an additional fee.
                    ReebeloCare coverage includes:`}
                  </p>
                  <ul className={listClass}>
                    <li>
                      {t`Extended warranty coverage for defects, malfunctions and
                      breakdowns for up to 24 months.`}
                    </li>
                    <li>
                      {t`Extensive accidental damage coverage including cracked
                      screens & spills.`}
                    </li>
                    <li>
                      {t`Hassle-free claims, same day service, mail-in or walk-in
                      at 1000+ repair centers.`}
                    </li>
                    <li>
                      {t`Replacement of your device or funds to buy another if your
                      device can't be repaired.`}
                    </li>
                    <li>
                      {t`Worldwide protection, with the ability to open a claim
                      even when overseas.`}
                    </li>
                  </ul>
                </>
              ),
            },
          ]
        : []),
    ],
  },
  ...(isUs
    ? [
        {
          sectionTitle: t`Sell Your Device`,
          icon: HandCoins,
          sectionContent: [
            {
              question: t`What devices can I sell on Reebelo?`,
              answer: (
                <p>
                  {t`You can sell smartphones, tablets, laptops, smartwatches and
                  more with Reebelo's trade-in program. Explore all
                  eligible devices at`}{' '}
                  <a href="/buyback" className="text-link-blue-500 underline">
                    {t`reebelo.com/buyback`}
                  </a>
                  {t`.`}
                </p>
              ),
            },
            {
              question: t`How is my quote calculated?`,
              answer: (
                <>
                  <p>
                    {t`A preliminary quote is calculated by matching your
                    device's condition against real-time market data to get
                    you the most competitive offer.`}
                  </p>
                  <p>
                    {t`After our trade-in partner completes inspection, your quote
                    will be confirmed. In the unlikely case that the final quote
                    is different from the preliminary quote, you can choose to
                    accept the counteroffer provided or you can reject the offer
                    and the device will be returned to you.`}
                  </p>
                </>
              ),
            },
            {
              question: t`How do I prepare my device before sending it in?`,
              answer: (
                <>
                  <p>
                    {t`To ensure a fast inspection and a quick payout, follow this
                    simple pre-shipment checklist:`}
                  </p>
                  <ul className={listClass}>
                    <li>
                      {t`Remove all passcodes and sign out of your iCloud or Google
                      accounts.`}
                    </li>
                    <li>
                      {t`Take off all cases and screen protectors so our team can
                      see the device clearly.`}
                    </li>
                    <li>
                      {t`Wipe all personal data to give the device a fresh start.`}
                    </li>
                    <li>
                      {t`Send only the device. Keep your SIM cards, cables, and
                      chargers—we don't need them!`}
                    </li>
                    <li>
                      {t`A simple wipe-down to remove dust or fingerprints helps
                      the process move faster.`}
                    </li>
                  </ul>
                </>
              ),
            },
            {
              question: t`How do I ship my device?`,
              answer: (
                <p>
                  {t`Once your trade-in is confirmed, you'll receive an email
                  with a pre-paid shipping label. Pack your device in a sturdy
                  box with enough padding (like bubble wrap or paper) to keep it
                  from moving around during transit. Tape your shipping label
                  clearly on the outside of the box, making sure it's easy
                  to scan. Take your package to the designated courier mentioned
                  on your label. Don't forget to ask for a receipt for your
                  tracking records!`}
                </p>
              ),
            },
            {
              question: t`When and how do I get paid?`,
              answer: (
                <p>
                  {t`Once your device is received and your final quote is
                  confirmed, you'll receive a secure link to enter your
                  banking details.`}
                </p>
              ),
            },
            {
              question: t`What happens to the data on my device?`,
              answer: (
                <p>
                  {t`Your privacy is our priority. While we recommend performing a
                  factory reset before shipping, our certified refurbishers also
                  conduct a professional data wipe as soon as your device
                  arrives. This ensures your personal information is permanently
                  removed before the device begins its next life.`}
                </p>
              ),
            },
            {
              question: t`What happens to my device after it's sold?`,
              answer: (
                <p>
                  {t`Once our expert refurbishers professionally wipe your data,
                  they'll repair and polish the device to get it ready for
                  a new owner. If it's seen better days and can't be
                  revived, they'll salvage functional parts and responsibly
                  recycle the rest to keep e-waste out of landfills.`}
                </p>
              ),
            },
          ],
        },
      ]
    : []),
  {
    sectionTitle: t`Buyer Protection Fee`,
    icon: ShieldCheck,
    sectionContent: [
      {
        question: t`What is the Buyer Protection Fee?`,
        answer: (
          <p>
            {t`The Buyer Protection Fee is a small fee charged by Reebelo on some
            orders. It helps cover the cost of running our quality checks and
            support services, including product verification, seller monitoring
            and customer support, so you can shop with confidence.`}
          </p>
        ),
      },
      {
        question: t`When will I see the Buyer Protection Fee?`,
        answer: (
          <p>
            {t`If your order includes a Buyer Protection Fee, it will be`}{' '}
            <strong>{t`clearly shown at checkout before you pay`}</strong>
            {t`, included in the total price of your order. There are no hidden
            fees.`}
          </p>
        ),
      },
      {
        question: t`How is the Buyer Protection Fee calculated?`,
        answer: (
          <p>
            {t`The fee may vary depending on the product type or order details. The
            exact amount will always be shown on the Product page, Cart and at
            Checkout before you complete your purchase.`}
          </p>
        ),
      },
      {
        question: t`Does the seller receive the Buyer Protection Fee?`,
        answer: (
          <p>
            {t`No. The Buyer Protection Fee is charged by Reebelo and is separate
            from the price set by the seller.`}
          </p>
        ),
      },
      {
        question: t`Why does Reebelo charge this fee?`,
        answer: (
          <p>
            {t`The fee helps us maintain high quality standards across our
            marketplace and provide ongoing customer support, giving you greater
            confidence when buying refurbished devices.`}
          </p>
        ),
      },
    ],
  },
];
